<template lang="pug">
  #document-upload-modal.modal-card
    header.modal-card-head
      p.modal-card-title {{ 'Upload Document' }}
      .box-close(@click="closeDocumentUploadModal")
        WtSvgIcon(icon="cross-icon" width="28px" height="28px")
    section.modal-card-body
      // Upload Form
      div(v-if="!uploadSuccess ")
        BField(v-if="!file", label="Document File")
          BUpload(
            v-model="file"
            drag-drop
            native
          )
            template(v-slot="{ isDragover }")
              .box.upload-template.has-text-centered(:class="{ 'is-highlighted': isDragover }")
                div
                  BIcon(pack="fas" icon="cloud-upload-alt" size="is-large")
                p Drag files here or click to upload
                p.help Max file size: 5MB

        // File Selected Preview
        .notification.is-info.is-light.mt-3(v-if="file")
          .level
            .level-left
              .level-item
                .is-flex.is-align-items-center(style="gap: 12px;")
                  BIcon(:icon="getFileTypeIcon(file.type)" size="is-medium" custom-class="has-text-info")
                  .is-flex-grow-1
                    p.has-text-weight-semibold {{ file.name }}
                    p.is-size-7.has-text-grey {{ formatFileSize(file.size) }}
            .level-right
              .level-item
                WtButton(
                  is-text
                  is-small
                  @click.native="clearFile"
                  title="Remove file"
                )
                  BIcon(icon="delete" type="is-danger")

        BField(label="Document Name" label-for="name")
          BInput(
            id="name"
            v-model="name"
            placeholder="E.g., Q1 Financial Report"
            @keyup.enter="submit"
          )

        BField(label="Document Type" label-for="type")
          BInput(
            id="type"
            v-model="type"
            placeholder="E.g., contracts, invoices, reports..."
            list="typesList"
            @keyup.enter="submit"
          )
        datalist#typesList
          option(v-for="t in types" :key="t" :value="t")

        p.help.has-text-danger(v-if="errorMessage")
          | {{ errorMessage }}

    footer.modal-card-foot
      div(v-if="!uploadSuccess")
        WtButton(color="grey" is-text @click.native="$parent.close()") Cancel
        WtButton(
          color="primary"
          :loading="loading"
          :disabled="!file || !name || !type || loading"
          @click.native="submit"
        ) Upload
      div(v-else)
        WtButton(color="primary" @click.native="resetForm") Upload Another
        WtButton(color="green" @click.native="$parent.close()") Done
</template>

<script>
import { mapActions } from "vuex";
import WtButton from "@@/WtButton/WtButton.vue";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon.vue";

export default {
  name: "DocumentUploadModal",
  components: {
    WtSvgIcon,
    WtButton
  },
  data() {
    return {
      file: null,
      name: "",
      type: "",
      description: "",
      types: [],
      loading: false,
      errorMessage: "",
      uploadSuccess: false,
      uploadedDocument: null,
      previewUrl: null,
      previewType: null
    };
  },
  computed: {
    ...mapActions("documents", ["uploadDocument"])
  },
  created() {
    this.fetchTypes();
  },
  methods: {
    async fetchTypes() {
      try {
        this.types = await this.$store.dispatch("documents/fetchDocumentTypes");
      } catch (err) {
        this.errorMessage = "Failed to load document types";
      }
    },
    async submit() {
      if (!this.file || !this.name || !this.type) {
        this.errorMessage = "Please fill in all required fields";
        return;
      }

      if (this.file.size > 5 * 1024 * 1024) {
        this.errorMessage = "File size exceeds 5MB limit";
        return;
      }

      this.loading = true;
      this.errorMessage = "";

      try {
        const formData = new FormData();
        formData.append("file", this.file);
        formData.append("name", this.name);
        formData.append("type", this.type);
        if (this.description) {
          formData.append("description", this.description);
        }

        const newDoc = await this.$store.dispatch(
          "documents/uploadDocument",
          formData
        );
        this.uploadedDocument = newDoc;

        // Capture preview from file in memory before state clears
        this.previewUrl = URL.createObjectURL(this.file);
        this.previewType = this.file.type;

        this.uploadSuccess = true;
        this.$store.dispatch("snackBar", {
          message: "Document uploaded successfully!",
          type: "is-success"
        });
        this.$emit("uploaded", newDoc);
      } catch (err) {
        const errMsg =
          err.response?.data?.data?.fields?.file?.[0] ||
          err.response?.data?.message ||
          "Upload failed";
        this.errorMessage = errMsg;
        this.$store.dispatch("snackBar", {
          message: errMsg,
          type: "is-danger"
        });
      } finally {
        this.loading = false;
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },
    getFileTypeIcon(mimeType) {
      if (!mimeType) return "file";
      if (mimeType.startsWith("image/")) return "image";
      if (mimeType === "application/pdf") return "file-pdf";
      if (
        mimeType === "application/msword" ||
        mimeType ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      )
        return "file-word";
      if (
        mimeType === "application/vnd.ms-excel" ||
        mimeType ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      )
        return "file-excel";
      if (
        mimeType === "application/vnd.ms-powerpoint" ||
        mimeType ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
      )
        return "file-powerpoint";
      if (mimeType.startsWith("video/")) return "file-video";
      if (mimeType.startsWith("audio/")) return "file-audio";
      if (
        mimeType === "application/json" ||
        mimeType === "application/xml" ||
        mimeType === "text/plain"
      )
        return "file-code";
      if (mimeType === "application/zip") return "file-archive";
      return "file";
    },

    clearFile() {
      this.file = null;
    },
    resetForm() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
        this.previewType = null;
      }
      this.file = null;
      this.name = "";
      this.type = "";
      this.description = "";
      this.errorMessage = "";
      this.uploadSuccess = false;
      this.uploadedDocument = null;
    },
    closeDocumentUploadModal() {
      this.$emit("uploaded");
    }
  }
};
</script>

<style lang="sass" scoped>
.modal-card-head
  padding-left: 240px
.upload-template
  display: flex
  justify-content: center
  align-items: center
  flex-direction: column
  width: 100%
  min-height: 120px

.file-icon-box
  display: flex
  align-items: center
  justify-content: center
  width: 80px
  height: 80px
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
  border-radius: 8px
  color: white
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3)

  ::v-deep .icon
    color: white
</style>
